/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global document, Office, __API_BASE_URL__ */

const API_BASE_URL =
  typeof __API_BASE_URL__ !== "undefined" && __API_BASE_URL__
    ? __API_BASE_URL__
    : "https://mailapi.solucionescerteza.com.mx";
const API_BASE_URL_NORMALIZED = API_BASE_URL.replace(/\/+$/, "");
const BITACORA_TIPOS = [
  "ACLARACION",
  "APLICACION DE PAGO",
  "CANCELACION",
  "COTIZACION",
  "EMISION",
  "ENDOSO",
  "OTRO",
  "REEXPEDICION",
  "RENOVACION",
];
let currentBitacoraId = null;
let currentAplicacionId = null;
let currentAplicacionesRows = [];
let handlersBound = false;
let appTabMode = "view";
let bitTabMode = "view";
let asesores = [];
let usuarios = [];

Office.onReady((info) => {
  if (info.host === Office.HostType.Outlook) {
    document.getElementById("sideload-msg").style.display = "none";
    document.getElementById("app-body").style.display = "block";

    // Carga inicial automatica al abrir el panel.
    run();
  }
});

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatIsoToDisplay(isoValue) {
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = pad2(date.getDate());
  const month = pad2(date.getMonth() + 1);
  const year = date.getFullYear();
  const hour = pad2(date.getHours());
  const minute = pad2(date.getMinutes());
  return `${day}/${month}/${year} ${hour}:${minute}`;
}

function formatDateAndTime(dateValue, timeValue) {
  if (!dateValue) {
    return "sin fecha";
  }

  const dateMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return String(dateValue);
  }

  const timeMatch = String(timeValue || "00:00").match(/^(\d{2}):(\d{2})/);
  const hour = timeMatch ? timeMatch[1] : "00";
  const minute = timeMatch ? timeMatch[2] : "00";

  return `${dateMatch[3]}/${dateMatch[2]}/${dateMatch[1]} ${hour}:${minute}`;
}

function getSortableDateTimeKey(dateValue, timeValue) {
  const dateMatch = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return "";
  }

  const timeMatch = String(timeValue || "00:00").match(/^(\d{2}):(\d{2})/);
  const hour = timeMatch ? timeMatch[1] : "00";
  const minute = timeMatch ? timeMatch[2] : "00";
  return `${dateMatch[1]}${dateMatch[2]}${dateMatch[3]}${hour}${minute}`;
}

function parseDateAndTime(dateValue, timeValue) {
  const dateMatch = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!dateMatch) {
    return null;
  }

  const timeMatch = String(timeValue || "00:00").match(/^(\d{2}):(\d{2})/);
  const hour = timeMatch ? timeMatch[1] : "00";
  const minute = timeMatch ? timeMatch[2] : "00";

  const parsed = new Date(`${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}T${hour}:${minute}:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function formatDuration(startDate, endDate) {
  if (!startDate || !endDate || Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "Sin datos";
  }

  const diffMs = endDate.getTime() - startDate.getTime();
  if (diffMs < 0) {
    return "Sin datos";
  }

  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${pad2(hours)}h ${pad2(minutes)}m`;
  }

  return `${pad2(hours)}h ${pad2(minutes)}m`;
}

function formatDurationFromSeconds(secondsValue) {
  const totalSeconds = Number(secondsValue);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "Sin datos";
  }

  const totalMinutes = Math.floor(totalSeconds / 60);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) {
    return `${days}d ${pad2(hours)}h ${pad2(minutes)}m`;
  }

  return `${pad2(hours)}h ${pad2(minutes)}m`;
}

function getNowPayloadAndDisplay() {
  const now = new Date();

  const year = now.getFullYear();
  const month = pad2(now.getMonth() + 1);
  const day = pad2(now.getDate());
  const hour = pad2(now.getHours());
  const minute = pad2(now.getMinutes());

  return {
    fecha: `${year}-${month}-${day}`,
    hora: `${hour}:${minute}`,
    display: formatIsoToDisplay(now.toISOString()),
  };
}

function extractBitacoraId(bodyText) {
  const bitacoraMatch = bodyText.match(/\[?\s*bitacora[_\s-]?id\s*[:=]\s*(\d+)\s*\]?/i);
  if (bitacoraMatch) {
    return bitacoraMatch[1];
  }

  const refMatch = bodyText.match(/\[?\s*RefID\s*[:=]\s*(\d+)\s*\]?/i);
  if (refMatch) {
    return refMatch[1];
  }

  return null;
}

function readMailBodyAsText(item) {
  return new Promise((resolve, reject) => {
    if (!item || !item.body) {
      resolve("");
      return;
    }

    item.body.getAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value || "");
      } else {
        reject(new Error("No se pudo leer el cuerpo del correo"));
      }
    });
  });
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getCurrentUserId() {
  const userProfile = Office.context.mailbox.userProfile;
  return (userProfile && (userProfile.emailAddress || userProfile.displayName)) || "usuario_desconocido";
}

function prepararCorreoParaRegistro({ id, tipo, estatus }) {
  if (!Office.context.mailbox || typeof Office.context.mailbox.displayNewMessageForm !== "function") {
    throw new Error("Outlook no permite preparar un correo nuevo en esta versión.");
  }

  const refId = escapeHtml(id);
  const tipoSeguro = escapeHtml(tipo);
  const estatusSeguro = escapeHtml(estatus);

  Office.context.mailbox.displayNewMessageForm({
    subject: `Seguimiento RefID ${id} - ${tipo}`,
    htmlBody: `<p>Hola,</p>
      <p>Comparto el seguimiento del siguiente registro:</p>
      <p><strong>[RefID: ${refId}]</strong><br>
      Tipo: ${tipoSeguro}<br>
      Estatus: ${estatusSeguro}</p>
      <p>Quedo pendiente de tus comentarios.</p>
      <p>Saludos.</p>`,
  });
}

async function getResponseErrorMessage(response) {
  const text = await response.text();
  if (!text) {
    return String(response.status);
  }

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.error === "string") {
      return parsed.error;
    }

    if (parsed && typeof parsed.details === "string") {
      return parsed.details;
    }
  } catch (parseError) {
    // Ignorar error de parseo y usar el texto original.
  }

  return text;
}

function renderHistorial(rows) {
  const historialSection = document.getElementById("historial-section");

  if (!Array.isArray(rows) || rows.length === 0) {
    historialSection.innerHTML = "<h3 class=\"historial-title\">Historial</h3><p>No hay historial disponible.</p>";
    historialSection.style.display = "block";
    return;
  }

  let html = "<h3 class=\"historial-title\">Historial</h3><div class=\"historial-list\">";
  rows.forEach((row) => {
    const estatus = escapeHtml(row.estatus_nuevo || row.status || "SIN ESTATUS");
    const fecha = row.fecha_actualizacion
      ? formatIsoToDisplay(row.fecha_actualizacion) || "sin fecha"
      : formatDateAndTime(row.fecha_respondido_nueva || row.fecha, row.hora_respondido_nueva || row.hora);
    const tipo = row.tipo_nuevo || row.tipo || "";
    const poliza = row.poliza_nueva || row.poliza || "";
    const usuario = escapeHtml(row.actualizado_por || row.actualiz || "sin usuario");
    const motivo = row.motivo_nuevo || row.motivo || "";

    html += "<article class=\"historial-item\">";
    html += `<div class=\"historial-top\"><span class=\"historial-status\">${estatus}</span><span class=\"historial-date\">${escapeHtml(fecha)}</span></div>`;
    if (tipo) {
      html += `<div class=\"historial-meta\">Tipo: ${escapeHtml(tipo)}</div>`;
    }
    if (poliza) {
      html += `<div class="historial-meta">Póliza: ${escapeHtml(poliza)}</div>`;
    }
    html += `<div class=\"historial-meta\">Actualizado por: ${usuario}</div>`;
    if (motivo) {
      html += `<div class=\"historial-meta\">Motivo: ${escapeHtml(motivo)}</div>`;
    }
    html += "</article>";
  });
  html += "</div>";

  historialSection.innerHTML = html;
  historialSection.style.display = "block";
}

async function fetchHistorialRows(bitacoraId) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/historial/${bitacoraId}`);
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

async function fetchCurrentStatus(bitacoraId) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/estatus/${bitacoraId}`);
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

function normalizeStatus(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function normalizeTipo(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();
}

function applyCurrentStatus(statusValue, emisorValue) {
  const statusSelect = document.getElementById("status-select");
  const userSelect = document.getElementById("bit-current-user-select");

  if (statusSelect && statusValue) {
    const desired = normalizeStatus(statusValue);
    const matchedOption = Array.from(statusSelect.options).find((opt) => normalizeStatus(opt.value) === desired);
    if (matchedOption) {
      statusSelect.value = matchedOption.value;
      document.getElementById("motivo-section").style.display = matchedOption.value === "ERROR" ? "block" : "none";
    }
  }

  if (userSelect && emisorValue) {
    userSelect.value = emisorValue;
  }
}

function applyCurrentTipo(tipoValue) {
  const tipoSelect = document.getElementById("tipo-select");
  const tipoInfo = document.getElementById("item-tipo");

  if (!tipoSelect || !tipoInfo) {
    return;
  }

  const normalized = normalizeTipo(tipoValue);
  const matchedTipo = BITACORA_TIPOS.find((tipo) => normalizeTipo(tipo) === normalized);

  if (matchedTipo) {
    tipoSelect.value = matchedTipo;
    tipoInfo.innerText = matchedTipo;
    return;
  }

  if (tipoValue) {
    tipoInfo.innerText = String(tipoValue);
  }
}

function applyCurrentPoliza(polizaValue) {
  const polizaInput = document.getElementById("poliza-input");
  const polizaInfo = document.getElementById("item-poliza");

  if (!polizaInput || !polizaInfo) {
    return;
  }

  const normalized = String(polizaValue || "").trim();
  polizaInput.value = normalized;
  polizaInfo.innerText = normalized || "Sin póliza";
}

function syncBitacoraPolizaInputState() {
  const statusSelect = document.getElementById("status-select");
  const tipoSelect = document.getElementById("tipo-select");
  const polizaInput = document.getElementById("poliza-input");

  if (!statusSelect || !tipoSelect || !polizaInput) {
    return false;
  }

  const status = normalizeStatus(statusSelect.value);
  const tipo = normalizeTipo(tipoSelect.value);
  const isEmisionCompleta = status === "COMPLETO" && tipo === "EMISION";
  const isOtherSyncTypes = ["CANCELACION", "RENOVACION", "REEXPEDICION"].includes(tipo);

  const allowPolizaEdit = isEmisionCompleta || isOtherSyncTypes;
  polizaInput.disabled = !allowPolizaEdit;
  polizaInput.title = allowPolizaEdit ? "" : "Solo editable para Emisión, Renovación, Reexpedición o Cancelación";
  return allowPolizaEdit;
}

async function fetchResponseMetrics(bitacoraId) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/metricas-respuesta/${bitacoraId}`);
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

async function fetchAplicacionesByRefId(bitacoraId) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/aplicaciones?refid=${encodeURIComponent(bitacoraId)}`);
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

async function fetchAplicacionesByPoliza(poliza) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/aplicaciones?poliza=${encodeURIComponent(poliza)}`);
  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

async function fetchAsesores() {
  try {
    const response = await fetch(`${API_BASE_URL_NORMALIZED}/asesores`);
    if (!response.ok) {
      throw new Error(await getResponseErrorMessage(response));
    }
    asesores = await response.json();
    renderAsesoresSelector();
  } catch (err) {
    console.error("Error al cargar asesores:", err);
  }
}

async function fetchUsuarios() {
  try {
    const response = await fetch(`${API_BASE_URL_NORMALIZED}/usuarios`);
    if (!response.ok) {
      throw new Error(await getResponseErrorMessage(response));
    }
    usuarios = await response.json();
    renderUsuariosSelector();
  } catch (err) {
    console.error("Error al cargar usuarios:", err);
  }
}

function renderUsuariosSelector() {
  const newBitSelect = document.getElementById("new-bit-correo");
  const currentBitSelect = document.getElementById("bit-current-user-select");

  if (newBitSelect) {
    newBitSelect.innerHTML = '<option value="">-- Selecciona un usuario --</option>';
    usuarios.forEach((user) => {
      const opt = document.createElement("option");
      opt.value = user.mail;
      opt.textContent = `${user.mail} (${user.emisor || "Sin nombre"})`;
      newBitSelect.appendChild(opt);
    });
  }

  if (currentBitSelect) {
    currentBitSelect.innerHTML = '<option value="">-- Selecciona un usuario --</option>';
    usuarios.forEach((user) => {
      const opt = document.createElement("option");
      opt.value = user.mail;
      opt.textContent = `${user.mail} (${user.emisor || "Sin nombre"})`;
      currentBitSelect.appendChild(opt);
    });
  }
}

function renderAsesoresSelector() {
  const select = document.getElementById("app-asesor-select");
  if (!select) return;
  
  select.innerHTML = '<option value="">-- Selecciona un asesor --</option>';
  asesores.forEach(ase => {
    const opt = document.createElement("option");
    opt.value = ase.id;
    opt.textContent = `${ase.nombre} (${ase.clave})`;
    select.appendChild(opt);
  });
}

function syncAsesorVisibility() {
  const section = document.getElementById("app-asesor-section");
  if (!section) return;
  
  // Mostrar solo si no hay bitacora vinculada
  section.style.display = currentBitacoraId ? "none" : "block";
}

async function createAplicacion(payload) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/aplicaciones`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

async function updateAplicacion(aplicacionId, payload) {
  const response = await fetch(`${API_BASE_URL_NORMALIZED}/aplicaciones/${aplicacionId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(await getResponseErrorMessage(response));
  }

  return response.json();
}

function setActiveTab(tabName) {
  const tabCorreoBtn = document.getElementById("tab-correo");
  const tabAplicacionBtn = document.getElementById("tab-aplicacion");
  const correoPanel = document.getElementById("correo-tab");
  const aplicacionPanel = document.getElementById("aplicacion-tab");
  const isCorreo = tabName === "correo";

  if (tabCorreoBtn) {
    tabCorreoBtn.classList.toggle("is-active", isCorreo);
    tabCorreoBtn.setAttribute("aria-selected", isCorreo ? "true" : "false");
  }
  if (tabAplicacionBtn) {
    tabAplicacionBtn.classList.toggle("is-active", !isCorreo);
    tabAplicacionBtn.setAttribute("aria-selected", !isCorreo ? "true" : "false");
  }

  correoPanel.classList.toggle("is-active", isCorreo);
  correoPanel.setAttribute("aria-hidden", isCorreo ? "false" : "true");
  aplicacionPanel.classList.toggle("is-active", !isCorreo);
  aplicacionPanel.setAttribute("aria-hidden", !isCorreo ? "false" : "true");
}

function setAppMessage(text, type) {
  const statusMsg = document.getElementById("app-status-msg");
  if (statusMsg) {
    statusMsg.innerText = text;
    statusMsg.className = `status-msg${type ? ` ${type}` : ""}`;
  }
}

function setAplicacionTabMode(mode) {
  const normalizedMode = mode === "view" ? "view" : "create";
  appTabMode = normalizedMode;
  const isViewMode = normalizedMode === "view";

  const viewTabBtn = document.getElementById("app-subtab-view");
  const createTabBtn = document.getElementById("app-subtab-create");
  const viewPanel = document.getElementById("app-view-panel");
  const createPanel = document.getElementById("app-create-panel");

  if (viewTabBtn) {
    viewTabBtn.classList.toggle("is-active", isViewMode);
    viewTabBtn.setAttribute("aria-selected", isViewMode ? "true" : "false");
  }

  if (createTabBtn) {
    createTabBtn.classList.toggle("is-active", !isViewMode);
    createTabBtn.setAttribute("aria-selected", !isViewMode ? "true" : "false");
  }

  if (viewPanel) {
    viewPanel.classList.toggle("is-active", isViewMode);
    viewPanel.setAttribute("aria-hidden", isViewMode ? "false" : "true");
  }

  if (createPanel) {
    createPanel.classList.toggle("is-active", !isViewMode);
    createPanel.setAttribute("aria-hidden", !isViewMode ? "false" : "true");
  }
}

function setBitacoraTabMode(mode) {
  const normalizedMode = mode === "view" ? "view" : "create";
  bitTabMode = normalizedMode;
  const isViewMode = normalizedMode === "view";

  const viewTabBtn = document.getElementById("bit-subtab-view");
  const createTabBtn = document.getElementById("bit-subtab-create");
  const viewPanel = document.getElementById("bit-view-panel");
  const createPanel = document.getElementById("bit-create-panel");

  if (viewTabBtn) {
    viewTabBtn.classList.toggle("is-active", isViewMode);
    viewTabBtn.setAttribute("aria-selected", isViewMode ? "true" : "false");
  }

  if (createTabBtn) {
    createTabBtn.classList.toggle("is-active", !isViewMode);
    createTabBtn.setAttribute("aria-selected", !isViewMode ? "true" : "false");
  }

  if (viewPanel) {
    viewPanel.classList.toggle("is-active", isViewMode);
    viewPanel.setAttribute("aria-hidden", isViewMode ? "false" : "true");
  }

  if (createPanel) {
    createPanel.classList.toggle("is-active", !isViewMode);
    createPanel.setAttribute("aria-hidden", !isViewMode ? "false" : "true");
  }
}

function setAplicacionControlsEnabled(enabled) {
  [
    "app-asunto-input",
    "app-poliza-view-input",
    "app-poliza-create-input",
    "app-observaciones-input",
    "app-status-select",
    "app-asesor-select",
    "app-selector",
    "app-subtab-view",
    "app-subtab-create",
    "ver-aplicacion-btn",
    "crear-aplicacion-btn",
  ].forEach((elementId) => {
    const control = document.getElementById(elementId);
    if (control && "disabled" in control) {
      control.disabled = !enabled;
    }
  });
}

function renderAplicacionesSelector(rows, selectedId) {
  const appSelector = document.getElementById("app-selector");
  currentAplicacionesRows = Array.isArray(rows) ? rows : [];
  appSelector.innerHTML = "";

  if (currentAplicacionesRows.length === 0) {
    const option = document.createElement("option");
    option.value = "";
    option.textContent = "Sin aplicaciones cargadas";
    appSelector.appendChild(option);
    appSelector.disabled = true;
    return;
  }

  currentAplicacionesRows.forEach((row) => {
    const option = document.createElement("option");
    option.value = String(row.id);
    const poliza = row.poliza ? String(row.poliza) : "Sin póliza";
    const estatus = row.estatus ? String(row.estatus) : "SIN ESTATUS";
    const fechaCreado = formatAppDateValue(row.fecha_creado);
    option.textContent = `ID ${row.id} | ${estatus} | ${poliza} | ${fechaCreado}`;
    if (selectedId && Number(selectedId) === Number(row.id)) {
      option.selected = true;
    }
    appSelector.appendChild(option);
  });

  appSelector.disabled = false;
}

function setCurrentAplicacion(aplicacionRow) {
  const appCurrentId = document.getElementById("app-current-id");
  const appAsuntoInput = document.getElementById("app-asunto-input");
  const appPolizaViewInput = document.getElementById("app-poliza-view-input");
  const appPolizaCreateInput = document.getElementById("app-poliza-create-input");
  const appObservacionesInput = document.getElementById("app-observaciones-input");
  const appStatusSelect = document.getElementById("app-status-select");
  const appSelector = document.getElementById("app-selector");

  if (!aplicacionRow) {
    currentAplicacionId = null;
    appCurrentId.innerText = "Sin registro";
    appAsuntoInput.value = "";
    appPolizaCreateInput.value = "";
    appObservacionesInput.value = "";
    appStatusSelect.value = "PENDIENTE";
    if (appSelector) {
      appSelector.value = "";
    }
    return;
  }

  currentAplicacionId = aplicacionRow.id;
  appCurrentId.innerText = `ID ${aplicacionRow.id}`;
  appAsuntoInput.value = aplicacionRow.asunto || "";
  appPolizaViewInput.value = aplicacionRow.poliza || "";
  appPolizaCreateInput.value = aplicacionRow.poliza || "";
  appObservacionesInput.value = aplicacionRow.observaciones || "";
  if (appSelector) {
    appSelector.value = String(aplicacionRow.id);
  }

  const desiredStatus = normalizeStatus(aplicacionRow.estatus);
  const matchingOption = Array.from(appStatusSelect.options).find((opt) => normalizeStatus(opt.value) === desiredStatus);
  if (matchingOption) {
    appStatusSelect.value = matchingOption.value;
  }
}

function formatAppDateValue(value) {
  if (!value) {
    return "Sin fecha";
  }

  const formatted = formatIsoToDisplay(value);
  return formatted || String(value);
}

function renderAplicacionesEncontradas(rows, selectedId) {
  const historialSection = document.getElementById("aplicaciones-historial-section");

  if (!Array.isArray(rows) || rows.length === 0) {
    historialSection.innerHTML = "<h3 class=\"historial-title\">Aplicaciones encontradas</h3><p>No hay aplicaciones para mostrar.</p>";
    historialSection.style.display = "block";
    return;
  }

  const selectedRow = rows.find((row) => Number(row.id) === Number(selectedId)) || rows[0];
  if (!selectedRow) {
    historialSection.innerHTML = "<h3 class=\"historial-title\">Aplicaciones encontradas</h3><p>No hay aplicaciones para mostrar.</p>";
    historialSection.style.display = "block";
    return;
  }

  let html = "<h3 class=\"historial-title\">Aplicaciones encontradas</h3><div class=\"historial-list\">";

  const id = escapeHtml(selectedRow.id || "-");
  const refid = escapeHtml(selectedRow.refid || "-");
  const poliza = selectedRow.poliza ? escapeHtml(selectedRow.poliza) : "-";
  const estatus = selectedRow.estatus ? escapeHtml(selectedRow.estatus) : "-";
  const observaciones = selectedRow.observaciones && String(selectedRow.observaciones).trim() ? escapeHtml(selectedRow.observaciones) : "-";
  const fechaCreado = escapeHtml(formatAppDateValue(selectedRow.fecha_creado));
  const fechaActualizado = escapeHtml(formatAppDateValue(selectedRow.fecha_actualizado));

  html += "<article class=\"historial-item\">";
  html += `<div class=\"historial-top\"><span class=\"historial-status\">Aplicación ID ${id}</span><span class=\"historial-date\">${fechaActualizado}</span></div>`;
  html += `<div class=\"historial-meta\">RefID: ${refid}</div>`;
  html += `<div class=\"historial-meta\">Póliza: ${poliza}</div>`;
  html += `<div class=\"historial-meta\">Estatus: ${estatus}</div>`;
  html += `<div class=\"historial-meta\">Observaciones: ${observaciones}</div>`;
  html += `<div class=\"historial-meta\">Fecha creación: ${fechaCreado}</div>`;
  html += `<div class=\"historial-meta\">Última actualización: ${fechaActualizado}</div>`;
  html += "</article>";

  html += "</div>";
  historialSection.innerHTML = html;
  historialSection.style.display = "block";
}

async function loadCurrentAplicacion(showMessage) {
  if (!currentBitacoraId) {
    renderAplicacionesSelector([], null);
    setCurrentAplicacion(null);
    if (showMessage) {
      setAppMessage("No se detecto RefID para buscar una aplicación existente.", "error");
    }
    return null;
  }

  const rows = await fetchAplicacionesByRefId(currentBitacoraId);
  if (!Array.isArray(rows) || rows.length === 0) {
    renderAplicacionesSelector([], null);
    setCurrentAplicacion(null);
    if (showMessage) {
      setAppMessage("No hay aplicación para este RefID.", "");
    }
    return null;
  }

  const selectedRow = rows.find((row) => Number(row.id) === Number(currentAplicacionId)) || rows[0];
  renderAplicacionesSelector(rows, selectedRow.id);
  setCurrentAplicacion(selectedRow);
  if (showMessage) {
    setAppMessage("Aplicación cargada.", "success");
  }

  return selectedRow;
}

async function searchAplicacionByPoliza(polizaValue, showMessage) {
  const polizaBusqueda = String(polizaValue || "").trim();
  const historialSection = document.getElementById("aplicaciones-historial-section");

  if (!polizaBusqueda) {
    if (showMessage) {
      setAppMessage("Debes capturar una póliza para buscar aplicación.", "error");
    }
    return null;
  }

  const rows = await fetchAplicacionesByPoliza(polizaBusqueda);
  if (!Array.isArray(rows) || rows.length === 0) {
    renderAplicacionesSelector([], null);
    setCurrentAplicacion(null);
    if (historialSection) {
      historialSection.style.display = "none";
      historialSection.innerHTML = "";
    }
    if (showMessage) {
      setAppMessage("No se encontraron aplicaciones para esa póliza.", "");
    }
    return null;
  }

  const selectedRow = rows.find((row) => Number(row.id) === Number(currentAplicacionId)) || rows[0];
  renderAplicacionesSelector(rows, selectedRow.id);
  setCurrentAplicacion(selectedRow);
  renderAplicacionesEncontradas(rows, selectedRow.id);

  if (showMessage) {
    setAppMessage(`${rows.length} aplicación(es) encontrada(s). Mostrando la seleccionada.`, "success");
  }

  return selectedRow;
}

function applyResponseMetrics(metrics) {
  const respondidoEnEl = document.getElementById("respondido-en");
  const tiempoRespuestaEl = document.getElementById("tiempo-respuesta");

  if (!metrics || !metrics.primera_fecha_respondido || !metrics.primera_hora_respondido) {
    respondidoEnEl.innerText = "Sin respuesta";
    tiempoRespuestaEl.innerText = "Sin respuesta";
    return;
  }

  respondidoEnEl.innerText = formatDateAndTime(metrics.primera_fecha_respondido, metrics.primera_hora_respondido);
  tiempoRespuestaEl.innerText = formatDurationFromSeconds(metrics.segundos_respuesta);
}

function getFirstResponseInfo(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  let selectedRow = null;
  let selectedKey = "";

  rows.forEach((row) => {
    const key = getSortableDateTimeKey(row.fecha_respondido_nueva, row.hora_respondido_nueva);
    if (!key) {
      return;
    }

    if (!selectedKey || key < selectedKey) {
      selectedKey = key;
      selectedRow = row;
    }
  });

  if (!selectedRow) {
    return null;
  }

  const responseDate = parseDateAndTime(selectedRow.fecha_respondido_nueva, selectedRow.hora_respondido_nueva);
  if (!responseDate) {
    return null;
  }

  return {
    date: responseDate,
    display: formatDateAndTime(selectedRow.fecha_respondido_nueva, selectedRow.hora_respondido_nueva),
  };
}

function setStatusMessage(text, type) {
  const statusMsg = document.getElementById("status-msg");
  statusMsg.innerText = text;
  statusMsg.className = `status-msg${type ? ` ${type}` : ""}`;
}

function syncMotivoVisibility() {
  const statusSelect = document.getElementById("status-select");
  const tipoSelect = document.getElementById("tipo-select");
  const motivoSection = document.getElementById("motivo-section");
  const motivoLabel = motivoSection.querySelector("label");
  const motivoInput = document.getElementById("motivo-input");

  if (!statusSelect || !tipoSelect || !motivoSection) {
    return;
  }

  const isError = normalizeStatus(statusSelect.value) === "ERROR";
  const isCancelacion = normalizeTipo(tipoSelect.value) === "CANCELACION";

  const showMotivo = isError || isCancelacion;
  motivoSection.style.display = showMotivo ? "block" : "none";

  if (motivoLabel && motivoInput) {
    if (isError) {
      motivoLabel.innerText = "Motivo (requerido para ERROR)";
      motivoInput.placeholder = "Describe el motivo del error";
    } else if (isCancelacion) {
      motivoLabel.innerText = "Motivo de Cancelación";
      motivoInput.placeholder = "Describe por qué se cancela";
    }
  }
}

function bindHandlersOnce() {
  if (handlersBound) {
    return;
  }

  handlersBound = true;

  const correoTabBtn = document.getElementById("tab-correo");
  if (correoTabBtn) {
    correoTabBtn.addEventListener("click", () => setActiveTab("correo"));
  }
 
  const aplicacionTabBtn = document.getElementById("tab-aplicacion");
  if (aplicacionTabBtn) {
    aplicacionTabBtn.addEventListener("click", () => setActiveTab("aplicacion"));
  }

  const statusSelect = document.getElementById("status-select");
  const tipoSelect = document.getElementById("tipo-select");
  const polizaInput = document.getElementById("poliza-input");
  const motivoInput = document.getElementById("motivo-input");
  const motivoSection = document.getElementById("motivo-section");
  const appSubtabView = document.getElementById("app-subtab-view");
  const appSubtabCreate = document.getElementById("app-subtab-create");
  const appAsuntoInput = document.getElementById("app-asunto-input");
  const appPolizaViewInput = document.getElementById("app-poliza-view-input");
  const appPolizaCreateInput = document.getElementById("app-poliza-create-input");
  const appObservacionesInput = document.getElementById("app-observaciones-input");
  const appStatusSelect = document.getElementById("app-status-select");
  const appSelector = document.getElementById("app-selector");

  appSubtabView.addEventListener("click", () => {
    setAplicacionTabMode("view");
    setAppMessage("", "");
  });

  appSubtabCreate.addEventListener("click", () => {
    setAplicacionTabMode("create");
    setAppMessage("", "");
  });

  const bitSubtabView = document.getElementById("bit-subtab-view");
  const bitSubtabCreate = document.getElementById("bit-subtab-create");

  if (bitSubtabView) {
    bitSubtabView.addEventListener("click", () => {
      setBitacoraTabMode("view");
      setStatusMessage("", "");
    });
  }

  if (bitSubtabCreate) {
    bitSubtabCreate.addEventListener("click", () => {
      setBitacoraTabMode("create");
      setStatusMessage("", "");
      // Pre-llenar fecha y hora actual
      const { fecha, hora } = getNowPayloadAndDisplay();
      const fechaInput = document.getElementById("new-bit-fecha-llegada");
      const horaInput = document.getElementById("new-bit-hora-llegada");
      if (fechaInput) fechaInput.value = fecha;
      if (horaInput) horaInput.value = hora;
    });
  }

  statusSelect.addEventListener("change", function () {
    syncMotivoVisibility();
    syncBitacoraPolizaInputState();
  });

  tipoSelect.addEventListener("change", () => {
    syncMotivoVisibility();
    syncBitacoraPolizaInputState();
  });

  syncMotivoVisibility();
  syncBitacoraPolizaInputState();

  document.getElementById("update-status-btn").addEventListener("click", async () => {
    if (!currentBitacoraId) {
      setStatusMessage("No se encontro bitacora_id/RefID en el correo.", "error");
      return;
    }

    const { fecha, hora } = getNowPayloadAndDisplay();

    const status = statusSelect.value;
    const tipo = tipoSelect.value;
    const emisor = document.getElementById("bit-current-user-select")?.value;
    const canEditPoliza = syncBitacoraPolizaInputState();
    const poliza = polizaInput.value.trim();
    const motivo = motivoInput.value.trim();
    const userProfile = Office.context.mailbox.userProfile;
    const userId = (userProfile && (userProfile.emailAddress || userProfile.displayName)) || "usuario_desconocido";

    if (status === "ERROR" && !motivo) {
      setStatusMessage("Debes ingresar un motivo para ERROR.", "error");
      return;
    }

    const payload = {
      bitacora_id: currentBitacoraId,
      status,
      tipo,
      emisor,
      fecha_respondido: fecha,
      hora_respondido: hora,
      actualiz: userId,
      ...(status === "ERROR" || normalizeTipo(tipo) === "CANCELACION" ? { motivo } : {}),
      ...(canEditPoliza && poliza ? { poliza } : {}),
    };

    try {
      const response = await fetch(`${API_BASE_URL_NORMALIZED}/actualizar-estatus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const text = await response.text();
        setStatusMessage(`Error al actualizar estatus: ${text || response.status}`, "error");
        return;
      }

      setStatusMessage("Estatus actualizado correctamente.", "success");
      applyCurrentTipo(tipo);
      if (canEditPoliza && poliza) {
        applyCurrentPoliza(poliza);
      }
      syncBitacoraPolizaInputState();

      // Recalcula métricas usando bitacora + primer historial.
      try {
        const metrics = await fetchResponseMetrics(currentBitacoraId);
        applyResponseMetrics(metrics);
      } catch (refreshError) {
        // No bloquear la UX si la recarga del historial falla.
      }
    } catch (err) {
      setStatusMessage("Error de conexion con la API.", "error");
    }
  });

  document.getElementById("ver-historial-btn").addEventListener("click", async () => {
    const historialSection = document.getElementById("historial-section");

    if (!currentBitacoraId) {
      historialSection.innerText = "No se encontro bitacora_id/RefID en el correo.";
      historialSection.style.display = "block";
      return;
    }

    try {
      const historial = await fetchHistorialRows(currentBitacoraId);
      renderHistorial(historial);
    } catch (err) {
      historialSection.innerText = "Error de conexion con la API.";
      historialSection.style.display = "block";
    }
  });

  document.getElementById("ver-aplicacion-btn").addEventListener("click", async () => {
    setAplicacionTabMode("view");

    const polizaBusqueda = appPolizaViewInput.value.trim();
    if (!polizaBusqueda) {
      setAppMessage("Captura una póliza para buscar aplicación.", "error");
      return;
    }

    try {
      await searchAplicacionByPoliza(polizaBusqueda, true);
    } catch (err) {
      setAppMessage(`Error al buscar aplicación por póliza: ${err instanceof Error ? err.message : "Error desconocido"}`, "error");
    }
  });

  appSelector.addEventListener("change", () => {
    const selectedId = Number(appSelector.value);
    if (!selectedId) {
      return;
    }

    const selectedRow = currentAplicacionesRows.find((row) => Number(row.id) === selectedId);
    if (selectedRow) {
      setCurrentAplicacion(selectedRow);
      renderAplicacionesEncontradas(currentAplicacionesRows, selectedId);
      setAppMessage(`Aplicación ID ${selectedId} seleccionada.`, "success");
    }
  });

  document.getElementById("crear-aplicacion-btn").addEventListener("click", async () => {
    if (appTabMode !== "create") {
      setAppMessage("Cambia a la sub-pestaña Nueva aplicación para crear un registro.", "error");
      return;
    }

    const asunto = appAsuntoInput.value.trim();
    const poliza = appPolizaCreateInput.value.trim();
    if (!poliza) {
      setAppMessage("Debes capturar una póliza para crear la aplicación.", "error");
      return;
    }

    if (!currentBitacoraId && !asunto) {
      setAppMessage("Si no hay RefID, debes capturar asunto para crear la aplicación.", "error");
      return;
    }

    const observaciones = appObservacionesInput.value.trim();
    const userId = getCurrentUserId();
    const asesorId = document.getElementById("app-asesor-select").value;

    const payload = {
      ...(currentBitacoraId ? { refid: Number(currentBitacoraId) } : {}),
      ...(asesorId ? { asesor_id: Number(asesorId) } : {}),
      asunto: asunto || null,
      poliza,
      observaciones: observaciones || null,
      estatus: appStatusSelect.value,
      usuario: userId,
    };

    try {
      const creada = await createAplicacion(payload);

      if (currentBitacoraId) {
        const refreshed = await fetchAplicacionesByRefId(currentBitacoraId);
        renderAplicacionesSelector(refreshed, creada.id);
      } else {
        renderAplicacionesSelector([creada], creada.id);
      }

      setCurrentAplicacion(creada);
      setAppMessage(`Aplicación creada con ID ${creada.id}.`, "success");

      const historialSection = document.getElementById("aplicaciones-historial-section");
      historialSection.style.display = "none";
      historialSection.innerHTML = "";
    } catch (err) {
      setAppMessage(`Error al crear aplicación: ${err instanceof Error ? err.message : "Error desconocido"}`, "error");
    }
  });

  const crearBitacora = async (prepararCorreo) => {
    if (bitTabMode !== "create") return;

    const correo = document.getElementById("new-bit-correo").value;
    const tipo = document.getElementById("new-bit-tipo").value;
    const estatus = document.getElementById("new-bit-estatus").value;
    const dia_llegada = document.getElementById("new-bit-fecha-llegada").value;
    const hora_llegada = document.getElementById("new-bit-hora-llegada").value;
    const fecha_respondido = document.getElementById("new-bit-fecha-respondido").value;
    const hora_respondido = document.getElementById("new-bit-hora-respondido").value;
    const statusMsg = document.getElementById("new-bit-status-msg");

    if (!correo || !tipo || !estatus || !dia_llegada || !hora_llegada) {
      statusMsg.innerText = "Faltan campos obligatorios.";
      statusMsg.className = "status-msg error";
      return;
    }

    const payload = {
      emisor: correo,
      tipo,
      estatus,
      dia_llegada,
      hora_llegada,
      fecha_respondido,
      hora_respondido,
      actualizo: getCurrentUserId(),
      asunto: Office.context.mailbox.item.subject || "Registro manual",
    };

    try {
      const response = await fetch(`${API_BASE_URL_NORMALIZED}/bitacora`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await response.text());
      }

      const result = await response.json();
      if (prepararCorreo) {
        try {
          prepararCorreoParaRegistro({ id: result.id, tipo, estatus });
        } catch (err) {
          statusMsg.innerText = `Registro creado con ID ${result.id}, pero no se pudo preparar el correo: ${err instanceof Error ? err.message : String(err)}`;
          statusMsg.className = "status-msg error";
          return;
        }
      }

      statusMsg.innerText = prepararCorreo
        ? `Registro creado con ID ${result.id}. Se preparó el correo con su RefID.`
        : `Registro creado correctamente con ID ${result.id}.`;
      statusMsg.className = "status-msg success";

      // Reset form or switch back
      setTimeout(() => {
        setBitacoraTabMode("view");
        run(); // Recargar para detectar el nuevo ID si es que se inserto en el cuerpo (manual)
      }, 2000);
    } catch (err) {
      statusMsg.innerText = `Error: ${err instanceof Error ? err.message : String(err)}`;
      statusMsg.className = "status-msg error";
    }
  };

  document.getElementById("crear-bitacora-btn").addEventListener("click", () => crearBitacora(false));
  document.getElementById("crear-bitacora-correo-btn").addEventListener("click", () => crearBitacora(true));
}

export async function run() {
  const item = Office.context.mailbox.item;
  const subjectEl = document.getElementById("item-subject");
  const itemIdEl = document.getElementById("item-id");
  const itemTipoEl = document.getElementById("item-tipo");
  const itemPolizaEl = document.getElementById("item-poliza");
  const respondidoEnEl = document.getElementById("respondido-en");
  const tiempoRespuestaEl = document.getElementById("tiempo-respuesta");
  const appRefIdEl = document.getElementById("app-refid");
  const tipoSelect = document.getElementById("tipo-select");
  const polizaInput = document.getElementById("poliza-input");
  const appPolizaViewInput = document.getElementById("app-poliza-view-input");
  const appPolizaCreateInput = document.getElementById("app-poliza-create-input");
  const appAsuntoInput = document.getElementById("app-asunto-input");
  const statusSection = document.getElementById("status-section");
  const historialSection = document.getElementById("historial-section");
  const aplicacionesHistorialSection = document.getElementById("aplicaciones-historial-section");

  respondidoEnEl.innerText = "Sin respuesta";
  tiempoRespuestaEl.innerText = "Sin respuesta";

  if (subjectEl) {
    subjectEl.innerText = item.subject || "(sin asunto)";
  }
 
  if (appAsuntoInput) {
    appAsuntoInput.value = item.subject || "";
  }
  itemIdEl.innerText = "No detectado";
  itemTipoEl.innerText = "Sin tipo";
  itemPolizaEl.innerText = "Sin póliza";
  appRefIdEl.innerText = "No detectado";
  tipoSelect.value = "OTRO";
  polizaInput.value = "";
  polizaInput.disabled = true;
  appPolizaViewInput.value = "";
  appPolizaCreateInput.value = "";
  setStatusMessage("", "");
  setAppMessage("", "");
  const urlParams = new URLSearchParams(window.location.search);
  const tabParam = urlParams.get("tab");
  const initialTab = tabParam === "aplicacion" ? "aplicacion" : "correo";
  setActiveTab(initialTab);
  historialSection.style.display = "none";
  historialSection.innerHTML = "";
  aplicacionesHistorialSection.style.display = "none";
  aplicacionesHistorialSection.innerHTML = "";

  // Siempre mostrar acciones del panel.
  statusSection.style.display = "block";

  currentBitacoraId = null;
  currentAplicacionesRows = [];
  appTabMode = "view";
  setCurrentAplicacion(null);
  renderAplicacionesSelector([], null);
  setAplicacionTabMode("view");
  appAsuntoInput.value = item.subject || "";
  setAplicacionControlsEnabled(true);
  setBitacoraTabMode("view");

  // Cargar asesores si no estan cargados
  if (asesores.length === 0) {
    fetchAsesores();
  } else {
    renderAsesoresSelector();
  }

  // Cargar usuarios si no estan cargados
  if (usuarios.length === 0) {
    fetchUsuarios();
  } else {
    renderUsuariosSelector();
  }

  try {
    const bodyText = await readMailBodyAsText(item);
    currentBitacoraId = extractBitacoraId(bodyText);

    if (currentBitacoraId) {
      itemIdEl.innerText = currentBitacoraId;
      appRefIdEl.innerText = currentBitacoraId;
      setAplicacionControlsEnabled(true);
      syncAsesorVisibility();

      try {
        const estatusActual = await fetchCurrentStatus(currentBitacoraId);
        applyCurrentStatus(estatusActual.status, estatusActual.emisor);
        applyCurrentTipo(estatusActual.tipo);
        applyCurrentPoliza(estatusActual.poliza);
        syncBitacoraPolizaInputState();
      } catch (statusError) {
        setStatusMessage("No se pudo cargar el estatus actual.", "error");
      }

      try {
        const metrics = await fetchResponseMetrics(currentBitacoraId);
        applyResponseMetrics(metrics);
      } catch (historialError) {
        setStatusMessage("No se pudo cargar la metrica de respuesta.", "error");
      }

      try {
        await loadCurrentAplicacion(false);
      } catch (aplicacionError) {
        setAppMessage("No se pudo cargar la aplicación actual.", "error");
      }
    } else {
      setStatusMessage("No se encontro bitacora_id ni RefID en el correo.", "error");
      appRefIdEl.innerText = "Sin RefID";
      setAppMessage("No se detecto RefID. Puedes crear una aplicación usando el asunto.", "");
      syncAsesorVisibility();
      syncBitacoraPolizaInputState();
    }
  } catch (err) {
    setStatusMessage("No se pudo leer el cuerpo del correo.", "error");
    setAppMessage("No se pudo leer el cuerpo del correo.", "error");
  }

  bindHandlersOnce();
}
